// Copyright (c) 2023 embyt GmbH. All rights reserved.
// Author: Marc Planeck

import * as React from "react";

import { EnergySource, IEnergyMeter, ITariffCircuit } from "parameter";
import { isSet, roundToDigits } from "utils";
import {
  formatPowerValue,
  formatEnergyValue,
  getColor,
  getPanelSelection,
  getGraphBoundaryDates,
  roundTimestamp,
  getDateString,
} from "guiutils";
import { IPanelSelection } from "states/cachedstates";
import { TimePeriod, DashboardPanel } from "types";
import { ITariffCircuitData } from "states/datastates";

import TariffCircuitListModel from "model/tariffcircuitlist";
import { ITrendData } from "states/trendingstates";
import { IDropdownSelectOption } from "./DropdownSelect";

import cssContent from "content.scss";
import { IState } from "states/states";
import { ActionType, IAction, IActionTypeSelectDashboardItem, raiseAction } from "actions";
import { WrappedComponentProps } from "react-intl";

const DIGITS_TO_SHOW = 2;

export interface IChartBaseExtProps {
  panel?: DashboardPanel;
  filterText: string;
}

export interface IChartBaseProps {
  tariffCircuits: ITariffCircuit[];
  values: ITariffCircuitData[] | undefined;
  energyMeters: IEnergyMeter[];
  selectedEnergySource: EnergySource | undefined;
  selectedHierarchyId: number | null | undefined;
  selection: IPanelSelection | undefined;
  trendMember: "energy" | "power" | "costs";
  startDate: number | undefined;
  endDate: number | undefined;
  trendData: ITrendData;
  timePeriod: TimePeriod;
  timezoneOffset: number | undefined;
  selectedTimestamp: number | undefined;
  useSelectedTimeStamp?: boolean;
  selectedDashboardIndex: number;
}

export interface IChartBaseState {
  showZeroValues: boolean;
}

export interface IChartBaseDispatch {
  onHierarchyChanged: (index: number, hierarchyId: number | null | undefined) => void;
}

export interface IChartTariffNode {
  id: number;
  value: number | undefined;
  name: string;
  valueText: string;
  color: string;
  parentId: number | null | undefined;
  maxValue?: number;
  isRelatedToTargetValue?: boolean;
  relatedDateString?: string;
  disabled?: boolean;
}

export interface ITrendTimestamps {
  startDate: number | undefined;
  endDate: number | undefined;
  allTimestamps: number[];
  usedTimestamps: number[];
}

export const mapStateToProps = (state: IState, ownProps: IChartBaseExtProps) => {
  const { index, selection } = getPanelSelection(state, ownProps.panel ?? DashboardPanel.POWER);
  const isEnergyPanel = ownProps.panel === DashboardPanel.ENERGY;
  const props = {
    tariffCircuits: state.parameter?.tariffline ?? [],
    values: isEnergyPanel ? undefined : state.data.tariffcircuit,
    energyMeters: state.parameter?.energymeter ?? [],
    selectedEnergySource: selection?.energySource,
    selectedHierarchyId: selection?.hierarchyId,
    selection,
    trendMember: isEnergyPanel ? state.gui.trendMember : "power",
    startDate: state.gui.trendPeriod.startDate,
    endDate: state.gui.trendPeriod.endDate,
    trendData: state.trending.trendData,
    timePeriod: state.gui.trendPeriod.timePeriod,
    timezoneOffset: state.site.tzOffset,
    selectedTimestamp: state.gui.selectedTimestamp,
    useSelectedTimeStamp: isEnergyPanel,
    selectedDashboardIndex: index,
    filterText: ownProps.filterText,
  };

  return props;
};

export const mapDispatchToProps = (dispatch: (action: IAction<any>) => void) => {
  const dispatchProps: IChartBaseDispatch = {
    onHierarchyChanged: (index: number, hierarchyId: number | null | undefined) => {
      const payload: IActionTypeSelectDashboardItem = { index, hierarchyId };
      dispatch(raiseAction(ActionType.ACT_SELECT_HIERARCHY, payload));
    },
  };

  return dispatchProps;
};

abstract class ChartBaseComponent<Props, State> extends React.Component<
  Props & IChartBaseExtProps & IChartBaseProps & WrappedComponentProps,
  State & IChartBaseState
> {
  protected getSelectedTariffCircuits(
    curEnergySource: string,
    showOnlySelectedTariffs: boolean,
    allowAllHierarchies?: boolean,
    ignoreCategories?: boolean,
    showAllChildren?: boolean,
    ignoreColoring?: boolean,
  ) {
    const tcModel = new TariffCircuitListModel(this.props.tariffCircuits, this.props.energyMeters);
    const tariffCircuitIndexes = tcModel.getTariffCircuits(
      curEnergySource,
      false,
      this.props.selectedHierarchyId,
      allowAllHierarchies === true,
      ignoreCategories === true ? undefined : this.props.selection?.category,
      Boolean(showAllChildren),
    );

    const availableTariffCircuits = tcModel.makeOptionsArray(
      tariffCircuitIndexes,
      showOnlySelectedTariffs === true ? this.props.selection?.tariffCircuitIds : undefined, // use all tariffs for now
      Boolean(showAllChildren),
    );

    const selectedTariffs = this.prepareData(availableTariffCircuits, Boolean(ignoreColoring));

    // filter zero-values if requested
    const tlEntries =
      this.state?.showZeroValues === false
        ? selectedTariffs.filter((entry) => {
            return isSet(entry.value) && entry.value > 0;
          })
        : selectedTariffs;

    return { tlEntries, tlOptions: availableTariffCircuits };
  }

  protected makeChartTariffNode(
    tcIndex: number,
    trendTimestamps: ITrendTimestamps,
    ignoreColoring: boolean,
  ) {
    const tcModel = new TariffCircuitListModel(this.props.tariffCircuits, this.props.energyMeters);
    const tariffCircuit = this.props.tariffCircuits[tcIndex];
    console.assert(tariffCircuit, "Tariff circuit not found in list", tcIndex);
    // round selectedTimestamp to 30 minutes interval
    const selectedTimeStamp = isSet(this.props.selectedTimestamp)
      ? Math.round((this.props.selectedTimestamp ?? 0) / 300) * 300
      : undefined;

    const { selectedValue, sumOfValues, maxTargetValue, maxRelateValue } =
      tcModel.getSelectedTimestampValues(
        this.props.trendData,
        tcIndex,
        this.props.trendMember,
        trendTimestamps.usedTimestamps,
        trendTimestamps.allTimestamps,
        selectedTimeStamp,
      );

    // check if live-data or selected data should get shown
    const value = isSet(this.props.values)
      ? this.props.trendMember === "costs"
        ? undefined
        : this.props.trendMember === "energy"
        ? this.props.values?.[tcIndex]?.energy
        : this.props.values?.[tcIndex]?.power
      : selectedValue;

    // prepare text..
    const toLabel =
      this.props.trendMember === "costs"
        ? `${isSet(value) && !isNaN(value) ? roundToDigits(value, DIGITS_TO_SHOW) : "-"} EUR`
        : this.props.trendMember === "energy"
        ? formatEnergyValue(value, tariffCircuit.unit)
        : formatPowerValue(value, tariffCircuit.unit);

    const showPower = this.props.trendMember !== "costs" && this.props.trendMember !== "energy";

    const relatedDateString =
      getDateString(
        this.props.timePeriod,
        trendTimestamps.startDate,
        trendTimestamps.endDate,
        !isSet(trendTimestamps.startDate),
        this.props.intl.formatMessage({
          id: `thisperiod_${this.props.timePeriod ?? ""}`,
        }),
        this.props.intl.locale,
      ) ?? "-";

    const tariffColorSettings = this.getTariffColor(
      tcIndex ?? 0,
      this.props.tariffCircuits,
      this.props.selection,
      this.props.filterText,
      ignoreColoring,
    );
    // prepare values for data/label entry of dataset
    return {
      id: tariffCircuit?.id ?? tcIndex,
      value: isNaN(value ?? 0) ? undefined : value,
      name: this.props.tariffCircuits[tcIndex].name ?? "---",
      valueText: toLabel,
      color: tariffColorSettings.color,
      parentId: tariffCircuit?.parent,
      maxValue: showPower ? maxRelateValue : sumOfValues,
      relatedDateString,
      isRelatedToTargetValue: maxTargetValue > 0,
      disabled:
        !tariffColorSettings.isSelectedTariff ||
        !tariffColorSettings.hasSelectedCategory ||
        !tariffColorSettings.isFilteredByText,
    } as IChartTariffNode;
  }

  private prepareData(tlOptionList: IDropdownSelectOption[], ignoreColoring: boolean) {
    // get timestamps for selected trend-data depending on the selected time-range
    const trendTimestamps = this.getTimestampsForTrendData(
      this.props.trendData,
      this.props.startDate,
      this.props.endDate,
      this.props.timezoneOffset,
      this.props.timePeriod,
      false,
    );

    return tlOptionList
      .filter((curItem) => curItem.visible)
      .map((curItem) => this.makeChartTariffNode(curItem.index, trendTimestamps, ignoreColoring));
  }

  protected getHeight(nrNodes: number) {
    return (
      nrNodes * (nrNodes < 4 ? 10 : nrNodes < 10 ? 5 : nrNodes < 15 ? 4 : nrNodes < 25 ? 3 : 2)
    );
  }

  protected getTariffColor(
    inTariffIndex: number,
    inTariffCircuits: ITariffCircuit[],
    inSelection: IPanelSelection | undefined,
    inFilterText: string | undefined,
    ignoreColoringSelectedTariffs?: boolean,
  ): {
    isSelectedTariff: boolean;
    hasSelectedCategory: boolean;
    isFilteredByText: boolean;
    color: string;
  } {
    const currentTariff = inTariffCircuits[inTariffIndex];

    const hasSelectedCategory =
      inSelection?.category === undefined ||
      (inSelection?.category === null &&
        ((currentTariff?.categories?.length ?? 0) < 1 || currentTariff?.categories?.[0] === "")) ||
      isSet(currentTariff?.categories?.find((searchCat) => searchCat === inSelection?.category));

    const isSelectedTariff =
      !isSet(inSelection?.tariffCircuitIds) ||
      inSelection?.tariffCircuitIds?.includes(currentTariff.id ?? inTariffIndex);

    const isFilteredByText =
      !isSet(inFilterText) ||
      currentTariff?.name.toLocaleLowerCase().includes(inFilterText.toLocaleLowerCase()) ||
      inFilterText === "";

    const color: string =
      hasSelectedCategory && (ignoreColoringSelectedTariffs || isSelectedTariff) && isFilteredByText
        ? getColor(inTariffIndex)
        : cssContent.GRAPH_GRAYED_OUT;

    return { isSelectedTariff, hasSelectedCategory, isFilteredByText, color };
  }

  protected getTimestampsForTrendData(
    trendData: ITrendData,
    inStartDate: number | undefined,
    inEndDate: number | undefined,
    timezoneOffset: number | undefined,
    timePeriod: TimePeriod,
    isOnlyOneSelected: boolean,
  ) {
    const graphDates = getGraphBoundaryDates(inStartDate, inEndDate, timezoneOffset, timePeriod);

    const allTimestamps = Object.keys(trendData)
      .map((item) => parseInt(item))
      .filter((curItem) => curItem >= graphDates.startDate! && curItem < graphDates.endDate!);

    const startDate = graphDates.startDate;
    const endDate = graphDates.endDate;

    let usedTimestamps = allTimestamps.map((item) => roundTimestamp(item, isOnlyOneSelected));
    usedTimestamps = [...new Set(usedTimestamps)]; // remove potential duplicates

    return { startDate, endDate, allTimestamps, usedTimestamps } as ITrendTimestamps;
  }
}

export default ChartBaseComponent;
